import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type ResumeDocument,
  type WrittenCutFreshness,
  getCorpusResume,
  postCorpusResumeWrite,
} from './api';

/**
 * The job-free corpus résumé as page state: the user's career composed AS a CV.
 * Ported 1:1 from apps/web/src/lib/useCorpusResume.ts (this lane's ground-truth
 * reference) — same four-state contract, same fields, same load/reload shape.
 *
 * There is no job to build against — the document is the SERVED composition of
 * the whole corpus (ordering + profile + skills are the backend's job; the FE
 * only renders it). Four honest states the view switches on: loading / error /
 * empty (route not yet live or no corpus) / data.
 *
 * NO INVENTION: a failure or a missing route surfaces the empty/error state — it
 * never synthesises a stand-in document. getCorpusResume's own doc comment notes
 * the route may 503 with no_anthropic_key; this hook degrades gracefully either way.
 *
 * resumeId (Save to Drive re-land, 2026-07-05): the real resumes.id the server
 * returns alongside document — was previously fetched and discarded (getCorpusResume's
 * client type was narrower than the wire contract). Carried through now so
 * CorpusResumePanel's DriveSaveButton has a real artifactId to point at; null only
 * before the first successful load (never a fabricated placeholder id).
 *
 * POLISH-2 (living-résumé Phase A, 2026-07-05) — the WRITTEN cut. GET
 * /corpus/resume returns the raw document ALWAYS, plus (additively) the
 * written cut's own {resumeId, document} whenever it is fresh — a pure read,
 * zero extra model calls. This hook holds BOTH in state and exposes:
 *   - `displayed` — the document the panel should show: WRITTEN when fresh
 *     (the "amazing résumé" default the moment one exists), else RAW.
 *   - `cut` / `setCut` — which one is displayed; a toggle flips this with NO
 *     network call (both documents are already in hand once the written one
 *     is fresh).
 *   - `writtenCut` — 'fresh' | 'stale' | 'none', driving the invite/nudge copy.
 *   - `writeCut()` — the explicit POST /corpus/resume/write action (the
 *     invite's "one tap" and the stale-nudge's "re-write"). NEVER called
 *     automatically — every mint is a visible user action (no-one-shot-
 *     wonders; never-auto-generate-on-every-view).
 */
export function useCorpusResume() {
  const [rawDocument, setRawDocument] = useState<ResumeDocument | null>(null);
  const [rawResumeId, setRawResumeId] = useState<string | null>(null);
  const [writtenDocument, setWrittenDocument] = useState<ResumeDocument | null>(null);
  const [writtenResumeId, setWrittenResumeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [writtenCut, setWrittenCut] = useState<WrittenCutFreshness>('none');
  // Which cut is DISPLAYED. Defaults to 'written' the instant a fresh one is
  // in hand (set in `load`/`writeCut` below); the toggle is a pure state flip.
  const [cut, setCut] = useState<'raw' | 'written'>('raw');
  const [writing, setWriting] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getCorpusResume();
      setRawDocument(r.document ?? null);
      setRawResumeId(r.resumeId ?? null);
      const freshness = r.writtenCut ?? 'none';
      setWrittenCut(freshness);
      if (freshness === 'fresh' && r.written) {
        setWrittenDocument(r.written.document);
        setWrittenResumeId(r.written.resumeId);
        setCut('written'); // the default: show the written cut the moment one is fresh
      } else {
        setWrittenDocument(null);
        setWrittenResumeId(null);
        setCut('raw');
      }
    } catch (err) {
      setRawDocument(null);
      setRawResumeId(null);
      setWrittenDocument(null);
      setWrittenResumeId(null);
      setWrittenCut('none');
      setCut('raw');
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  /** The explicit "write it properly" / "re-write" action — POST
   *  /corpus/resume/write. Entitlement + rate-limit gated server-side; this
   *  surfaces whatever error the server returns (e.g. entitlement_required)
   *  rather than swallowing it, same honesty posture as `load`'s own error
   *  handling. On success, the freshly-written document becomes the displayed
   *  cut — the SAME loop the invite/nudge can be tapped again once the corpus
   *  moves and `writtenCut` next reports 'stale'. */
  const writeCut = useCallback(async () => {
    setWriting(true);
    setWriteError(null);
    try {
      const r = await postCorpusResumeWrite();
      setWrittenDocument(r.document ?? null);
      setWrittenResumeId(r.resumeId ?? null);
      setWrittenCut(r.writtenCut ?? 'fresh');
      setCut('written');
    } catch (err) {
      setWriteError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriting(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The document + resumeId the panel actually renders, per `cut`. Falls back
  // to raw if 'written' is selected but somehow absent (defensive; the toggle
  // is only ever shown once writtenDocument is non-null) — never a blank panel.
  const document = useMemo(
    () => (cut === 'written' && writtenDocument ? writtenDocument : rawDocument),
    [cut, writtenDocument, rawDocument],
  );
  const resumeId = useMemo(
    () => (cut === 'written' && writtenResumeId ? writtenResumeId : rawResumeId),
    [cut, writtenResumeId, rawResumeId],
  );

  return {
    document,
    resumeId,
    loading,
    error,
    reload: load,
    writtenCut,
    cut,
    setCut,
    hasWritten: writtenDocument != null,
    writing,
    writeError,
    writeCut,
  };
}
